'use client';
import clsx from 'clsx';
import React, { useRef, cloneElement } from 'react';
import { factory } from '../../factory/factory.utils.mjs';
import { Typography } from '../../Foundations/Typography/Typography.mjs';
import '@floating-ui/react';
import { getFloatingPosition } from '../subcomponents/Floating/get-floating-position/get-floating-position.mjs';
import { FloatingArrow } from '../subcomponents/Floating/FloatingArrow/FloatingArrow.mjs';
import { isElement } from '../subcomponents/Popover/PopoverTarget/PopoverTarget.utils.mjs';
import { useMergedRef } from '../subcomponents/Popover/PopoverTarget/use-merged-ref.mjs';
import classes from './Tooltip.module.css.mjs';
import { useTooltip } from './use-tooltip.mjs';

const Tooltip = factory((props, ref) => {
  const {
    children,
    position,
    refProp = "ref",
    label,
    onPositionChange,
    opened,
    className,
    multiline = true,
    zIndex,
    onClick,
    onMouseEnter,
    onMouseLeave,
    inline,
    offset = 5,
    ...rest
  } = props;
  const dir = "ltr";
  const events = { hover: true, focus: false, touch: false };
  const arrowSize = 16;
  const arrowRef = useRef(null);
  const withArrow = false;
  const arrowOffset = 5;
  const arrowPosition = "side";
  const tooltip = useTooltip({
    position: getFloatingPosition(dir, position),
    closeDelay: void 0,
    openDelay: void 0,
    onPositionChange,
    opened,
    events,
    arrowRef,
    arrowOffset: void 0,
    offset: typeof offset === "number" ? offset + (0) : offset,
    positionDependencies: [...[], children],
    inline,
    strategy: void 0
  });
  if (!isElement(children)) {
    throw new Error(
      "[@kohler/core] Tooltip component children should be an element or a component that accepts ref, fragments, strings, numbers and other primitive values are not supported"
    );
  }
  const targetRef = useMergedRef(tooltip.reference, children.ref, ref);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Typography,
    {
      component: "div",
      size: "paragraph-small",
      ...rest,
      ...tooltip.getFloatingProps({
        ref: tooltip.floating,
        className: clsx(classes.tooltip, tooltip.opened && classes.tooltipOpened, className, "kohler-tooltip-root"),
        style: {
          zIndex,
          top: tooltip.y ?? 0,
          left: tooltip.x ?? 0
        }
      }),
      "data-multiline": multiline
    },
    label,
    /* @__PURE__ */ React.createElement(
      FloatingArrow,
      {
        ref: arrowRef,
        arrowX: tooltip.arrowX,
        arrowY: tooltip.arrowY,
        visible: withArrow,
        position: tooltip.placement,
        arrowSize,
        arrowOffset,
        arrowRadius: 0,
        arrowPosition
      }
    )
  ), cloneElement(
    children,
    tooltip.getReferenceProps({
      onClick,
      onMouseEnter,
      onMouseLeave,
      onMouseMove: props.onMouseMove,
      onPointerDown: props.onPointerDown,
      onPointerEnter: props.onPointerEnter,
      [refProp]: targetRef,
      className: clsx(children.props.className),
      ...children.props
    })
  ));
});

export { Tooltip };
